import React, { useState } from "react";
import {
  StyleSheet,
  View,
  Modal,
  TouchableWithoutFeedback,
  Pressable,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import Text from "@components/CustomText";
import MiniScooter from "@assets/svg/CpDeliveryBoys/mini_scooter";
import { colors } from "@constants/theme";
import { Menu, MenuItem } from "react-native-material-menu";
import SettingBlack from "@assets/svg/SettingBlack";
import { deleteCpDb } from "@services/api";
import Cross from "@assets/svg/CrossIcon";
import t from "@constants/localization";
import AddDeliveryBoy from "./AddDeliveryBoy";
import { useSelector } from "react-redux";

const DbList = ({ cpDeliveryBoys, fetchData }) => {
  const [showAddDbModal, setShowAddDbModal] = useState(false);
  const [showDeleteDbModal, setShowDeleteDbModal] = useState(false);
  const [selectedDb, setSelectedDb] = useState(null);
  const [deleteDbModalText, setDeleteDbModalText] = useState("");
  const [menuStates, setMenuStates] = useState(
    Array(cpDeliveryBoys.length).fill(false),
  );

  const PATH = "screens/CpDeliveryBoys/dbList";

  const handleMenuPress = (index) => {
    const newMenuStates = [...menuStates];
    newMenuStates[index] = !newMenuStates[index];
    setMenuStates(newMenuStates);
  };
  const { disable_add_db_btn = false } = useSelector(
    ({ configReducer }) => configReducer,
  );

  const handleMenuItemPress = async (index, action) => {
    const updatedMenuStates = [...menuStates];
    updatedMenuStates[index] = false;
    setMenuStates(updatedMenuStates);
    if (action === "Delete") {
      const { data } = await deleteCpDb({ db_id: cpDeliveryBoys[index].id });
      if (data?.statusCode === 403) {
        setDeleteDbModalText(data?.warningMessage);
        setShowDeleteDbModal(true);
      } else {
        fetchData && fetchData();
      }
      setSelectedDb(cpDeliveryBoys[index]);
    }
  };

  const onPressDeleteDb = async () => {
    await deleteCpDb({ db_id: selectedDb.id, overide: true });
    fetchData && fetchData();
    setShowDeleteDbModal(false);
  };

  const onPressAddDeliveryBoy = () => {
    setShowAddDbModal(true);
  };
  const renderDBList = (cpdb, index) => (
    <View style={styles.deliveryBoyItem}>
      <View style={{ width: "80%" }}>
        <Text variant="body_text_1" numberOfLines={1}>
          {cpdb.db_name || ""}
        </Text>
        <Text
          variant="caption_2"
          numberOfLines={1}
          style={{ color: colors.mediumBlacklightBlack }}
        >
          {cpdb.db_phone_number || ""}
        </Text>
      </View>
      <Menu
        style={{ marginTop: 20 }}
        visible={menuStates[index]}
        onRequestClose={() => handleMenuPress(index)}
        anchor={
          <Pressable
            testID="cpdb-menu-btn"
            onPress={() => handleMenuPress(index)}
            style={styles.menuIconContainer}
          >
            <SettingBlack width={12} />
          </Pressable>
        }
      >
        <MenuItem onPress={() => handleMenuItemPress(index, "Delete")}>
          {t(`${PATH}/removeDb`)}
        </MenuItem>
      </Menu>
    </View>
  );

  return (
    <ScrollView
      contentContainerStyle={styles.scrollViewContent}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <MiniScooter />
          <Text variant="body_text_4" style={styles.headerText}>
            {t(`${PATH}/yourDbs`)}
          </Text>
        </View>
        <View style={styles.deliveryBoyList}>
          {cpDeliveryBoys.map((cpdb, index) => renderDBList(cpdb, index))}
        </View>
        {disable_add_db_btn ? null : (
          <TouchableOpacity
            testID="cpdb-add-btn"
            onPress={onPressAddDeliveryBoy}
            style={styles.addButton}
          >
            <Text variant="subtitle_16" style={styles.addButtonText}>
              {t(`${PATH}/addNew`)}
            </Text>
          </TouchableOpacity>
        )}
        <AddDbModal
          showModal={showAddDbModal}
          setShowModal={setShowAddDbModal}
          fetchData={fetchData}
        />
        <DeleteDbModal
          showDeleteDbModal={showDeleteDbModal}
          setShowDeleteDbModal={setShowDeleteDbModal}
          selectedDb={selectedDb}
          onPressDeleteDb={onPressDeleteDb}
          path={PATH}
          modalText={deleteDbModalText}
        />
      </View>
    </ScrollView>
  );
};

const BoldTextExtractor = ({ text }) => {
  const boldRegex = /<b>(.*?)<\/b>/g;

  const boldMatches = text.match(boldRegex) || [];

  let cleanedText = text;

  boldMatches.forEach((match) => {
    cleanedText = cleanedText.replace(match, "");
  });
  return (
    <Text variant="body_text_1" style={{ padding: 10 }}>
      {boldMatches.map((match, index) => (
        <Text style={{ fontWeight: "bold" }}>
          {match.replace(/<\/?b>/g, "")}
        </Text>
      ))}
      {cleanedText}
    </Text>
  );
};

const DeleteDbModal = ({
  showDeleteDbModal,
  setShowDeleteDbModal,
  selectedDb,
  onPressDeleteDb,
  path,
  modalText,
}) => (
  <Modal
    statusBarTranslucent
    navigationBarTranslucent
    animationType="fade"
    transparent={true}
    visible={showDeleteDbModal}
    onRequestClose={() => {
      setShowDeleteDbModal(!showDeleteDbModal);
    }}
  >
    <TouchableWithoutFeedback onPress={() => setShowDeleteDbModal(false)}>
      <View style={styles.modalOverlay} />
    </TouchableWithoutFeedback>
    <View style={styles.deleteModalContainer}>
      <BoldTextExtractor text={modalText} />
      <View style={styles.deleteModalRow}>
        <TouchableWithoutFeedback onPress={() => setShowDeleteDbModal(false)}>
          <Text
            variant="body_text_4"
            style={{ padding: 10, color: colors.darkGrey }}
          >
            {t(`${path}/modalNo`)}
          </Text>
        </TouchableWithoutFeedback>
        <TouchableWithoutFeedback onPress={onPressDeleteDb}>
          <Text
            variant="body_text_4"
            style={{ padding: 10, color: colors.primary }}
          >
            {t(`${path}/modalYes`)}
          </Text>
        </TouchableWithoutFeedback>
      </View>
    </View>
  </Modal>
);

const AddDbModal = ({ showModal, setShowModal, fetchData }) => (
  <Modal
    statusBarTranslucent
    navigationBarTranslucent
    visible={showModal}
    animationType="slide"
    transparent
  >
    <TouchableWithoutFeedback
      onPress={() => {
        setShowModal(false);
      }}
    >
      <View style={styles.modalOverlay} />
    </TouchableWithoutFeedback>
    <View style={styles.modalContent}>
      <View style={styles.modalCross}>
        <TouchableWithoutFeedback onPress={() => setShowModal(false)}>
          <Cross width={16} height={16} />
        </TouchableWithoutFeedback>
      </View>
      <AddDeliveryBoy setShowAddDbModal={setShowModal} fetchData={fetchData} />
    </View>
  </Modal>
);

const styles = StyleSheet.create({
  scrollViewContent: {
    flexGrow: 1,
  },
  container: {
    margin: 16,
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.lightGrey,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerText: {
    marginLeft: 12,
  },
  deliveryBoyList: {
    marginBottom: 12,
  },
  deliveryBoyItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    marginTop: 16,
    borderRadius: 8,
  },
  addButton: {
    width: "30%",
    margin: 8,
    borderColor: colors.plain_red,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: "center",
  },

  addButtonText: {
    color: colors.plain_red,
    padding: 8,
  },
  modalOverlay: {
    backgroundColor: "rgba(0,0,0, 0.4)",
    flex: 1,
  },
  modalContent: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
  },
  menuIconContainer: {
    backgroundColor: "rgba(0, 171, 89, 0.1)",
    justifyContent: "center",
    height: 30,
    width: 30,
    alignItems: "center",
    borderRadius: 25,
  },
  deleteModalContainer: {
    width: "90%",
    margin: 20,
    backgroundColor: "white",
    borderRadius: 8,
    padding: 12,
    paddingHorizontal: 20,
    alignItems: "center",
    alignContent: "center",
    position: "absolute",
    top: "35%",
  },
  deleteModalRow: {
    width: "100%",
    flexDirection: "row",
    marginTop: 10,
    justifyContent: "flex-end",
  },
  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: "#fff",
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
});

export default DbList;
